import React, { Suspense, useState } from 'react';
import {
  Button,
  Col,
  ConfigProvider,
  notification,
  Row,
  Table,
  Tooltip,
} from 'antd';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from '../../../../helpers/userHaveRolePermission';
import { PAGE_URLS, PARENT_ROUTES } from '../../../../helpers/endpoints';
import { DownloadOutlined, SendOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import trTR from 'antd/es/locale/tr_TR';
import { ORDER_TYPES, STATUS } from '../../../../helpers/constants';
import {
  getAllStockInventoryForCsv,
  getFilterStockTracking,
} from '../../../../store/requests/stock';
import moment from 'moment';
const StockTrackingFilterForm = React.lazy(
  () => import('./StockTrackingFilterForm'),
);

const initialPageParams = {
  pagination: {
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Toplam ${total} kayıt`,
    pageSizeOptions: [20, 50, 100],
    size: 'small',
    current: 1,
    pageSize: 20,
  },
};

const StockTracking = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [filteringValue, setFilteringValue] = useState({});
  const [trackingData, setTrackingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [csvDataInfo, setCsvDataInfo] = useState({
    totalElements: null,
    loading: false,
  });
  const [tableParams, setTableParams] = useState(initialPageParams);

  const fetchData = (tableParamsValue, filter) => {
    const queryString = getQueryString(tableParamsValue, filter);
    getStockTransactions(queryString);
  };

  const getQueryString = (tableParamsValue, filter) => {
    let queryString = 'filter=';

    setFilteringValue(filter);
    const { region, ...rest } = filter;

    if (Object.values(rest).some((item) => !!item)) {
      queryString += Object.entries(rest)
        .filter(
          ([item, value]) => value != null && value !== undefined && rest[item],
        )
        .map(([item, value]) => `${item}=='${encodeURI(value)}'`)
        .join(' and ');
    }

    queryString = `${queryString}&page=${tableParamsValue.pagination.current - 1}&size=${tableParamsValue.pagination.pageSize}`;

    if (tableParamsValue.column) {
      queryString = `${queryString}&sort=${
        tableParamsValue.column.dataIndex === 'externalSkuId'
          ? 'offer.productMappings.externalSkuId'
          : tableParamsValue.column.dataIndex === 'offerId'
            ? 'offer.offerId'
            : tableParamsValue.column.dataIndex === 'unitType'
              ? 'offer.product.productUnitType'
              : tableParamsValue.column.dataIndex === 'productName'
                ? 'offer.product.name'
                : tableParamsValue.column.dataIndex
      },${ORDER_TYPES[tableParamsValue.order]}`;
    }
    return queryString;
  };

  const getStockTransactions = (queryString) => {
    if (!loading) {
      setLoading(true);
      getFilterStockTracking(queryString)
        .then((stock) => {
          const { content, page } = stock;
          setTrackingData(content);
          setTableParams({
            pagination: {
              total: page.totalElements,
              current: page.number + 1,
              pageSize: page.size,
            },
          });
          setCsvDataInfo((prev) => ({
            totalElements: page.totalElements,
            loading: false,
          }));
        })
        .finally(() => setLoading(false));
    }
  };

  const columns = [
    {
      title: t('Offer ID'),
      dataIndex: 'offerId',
      showSorterTooltip: false,
      sorter: !!trackingData.length,
    },
    {
      title: t('ERP Kodu'),
      dataIndex: 'externalSkuId',
      showSorterTooltip: false,
      sorter: !!trackingData.length,
    },
    {
      title: t('productName'),
      dataIndex: 'productName',
      showSorterTooltip: false,
      sorter: !!trackingData.length,
    },
    {
      title: t('productUnitType'),
      dataIndex: 'unitType',
      showSorterTooltip: false,
      sorter: !!trackingData.length,
      render: (text, record) => record.unitTypeLabel,
    },
    {
      title: t('totalStockAmount'),
      dataIndex: 'quantity',
      showSorterTooltip: false,
      sorter: !!trackingData.length,
    },
    {
      title: t('sellableStockAmount'),
      dataIndex: 'freeQuantity',
      showSorterTooltip: false,
      sorter: !!trackingData.length,
    },
    {
      title: t('goToStockTransactions'),
      key: 'action',
      render: (text, record) => (
        <Button
          style={{ marginLeft: 20 }}
          shape={'round'}
          size="small"
          icon={<SendOutlined />}
          onClick={() => directionToStockTransaction(record)}
        />
      ),
    },
  ];

  const directionToStockTransaction = (stockData) => {
    const { offerId, skuId, externalSkuId } = stockData;

    const todayStart = moment().startOf('day').add(3, 'hours');
    const tomorrowStart = moment()
      .add(1, 'days')
      .startOf('day')
      .add(3, 'hours');

    history.push({
      pathname: `${PARENT_ROUTES.STOCK_MANAGEMENT}${PAGE_URLS.STOCK_TRANSACTION_SCREEN}`,
      state: {
        stock: {
          offerId,
          skuId: externalSkuId,
          regionId: filteringValue.region,
          store: filteringValue['offer.store.code'],
          startAndEndDate: [
            todayStart.toISOString(),
            tomorrowStart.toISOString(),
          ],
        },
      },
    });
  };

  const getAllStockTracking = async () => {
    setCsvDataInfo((prev) => ({ ...prev, loading: true }));

    const keyMapping = {
      'offer.store.code': 'storeId',
      'offer.offerId': 'offerId',
      'offer.productMappings.externalSkuId': 'externalSkuId',
    };

    const payload = Object.fromEntries(
      Object.entries(filteringValue)
        .filter(([key, value]) => value !== undefined)
        .map(([key, value]) => [
          keyMapping[key] || key,
          key === 'offer.store.code' ? String(value) : value,
        ]),
    );

    payload.queryString = `page=0&size=${csvDataInfo.totalElements}`;

    const { region, ...payloadWithoutRegion } = payload;

    getAllStockInventoryForCsv(payloadWithoutRegion)
      .then((res) => {
        notificationToast(STATUS.SUCCESS);
      })
      .finally(() => setCsvDataInfo((prev) => ({ ...prev, loading: false })));
  };

  const notificationToast = (type) => {
    notification[type]({
      message: t('processCompleted'),
      description: t('reportLinkSentMailMessage'),
    });
  };

  const actions = [
    {
      isCrudEnabled: true,
      id: 'downloadButton',
      type: '',
      title: t('download'),
      customContent: (
        <>
          <Button
            shape="round"
            onClick={getAllStockTracking}
            icon={<DownloadOutlined />}
            loading={csvDataInfo.loading}
            style={{ marginBlock: 5 }}
            disabled={!trackingData.length > 0}
          />
        </>
      ),
      disabled: false,
      hasDropdown: false,
    },
  ];

  const renderActionButtons = () => {
    return actions.map((item, idx) => {
      return (
        <Tooltip title={item.title} key={idx}>
          {item.isCrudEnabled &&
            (item.customContent ?? (
              <Button
                type={item?.type}
                shape="round"
                icon={item?.icon}
                onClick={item.onClick}
                disabled={item.disabled}
                danger={item?.danger}
              />
            ))}
        </Tooltip>
      );
    });
  };

  return (
    <>
      <Suspense fallback={<></>}>
        <StockTrackingFilterForm
          handlerFilter={(filter) => {
            fetchData(initialPageParams, filter);
          }}
        />
      </Suspense>
      {renderActionButtons()}
      {
        <Row>
          <Col>
            <ConfigProvider locale={trTR}>
              <Table
                style={{ width: '100%' }}
                size="small"
                columns={columns}
                dataSource={trackingData}
                pagination={tableParams.pagination}
                loading={loading}
                onChange={(pagination, filters, sorter) => {
                  fetchData(
                    {
                      pagination,
                      ...sorter,
                    },
                    filteringValue,
                  );
                }}
                rowKey={(record) => record?.id}
                scroll={{
                  y: '70vh',
                }}
              />
            </ConfigProvider>
          </Col>
        </Row>
      }
    </>
  );
};

export default StockTracking;
